import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getGeoLocation, submitLead } from '../services/api';
import { countryCodes } from '../data/countryCodes';

const LeadPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryDialCode: '1',
    country: 'US',
    phoneNumber: '',
    description: '',
  });
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchGeoData = async () => {
      const data = await getGeoLocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const matchedCountry = countryCodes.find(c => c.countryCode === data.data.countryCode);
        if (matchedCountry) {
          setFormData((prev) => ({
            ...prev,
            country: matchedCountry.countryCode,
            countryDialCode: matchedCountry.code.replace('+', ''),
          }));
        }
      }
    };
    fetchGeoData();
  }, []);

  const getCountryDialCode = (countryCode) => {
    const dialCodes = {
      US: '1',
      GB: '44',
      CA: '1',
      AU: '61',
      IN: '91',
      PK: '92',
      NL: '31',
      DE: '49',
      FR: '33',
    };
    return dialCodes[countryCode] || '1';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: numericValue }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        countryDialCode: parseInt(formData.countryDialCode),
        country: formData.country,
        phoneNumber: formData.phoneNumber,
        ipAddress: geoData?.data?.ipAddress || '',
        description: formData.description,
        pageUrl: window.location.href,
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        geoLocation: geoData ? JSON.stringify(geoData) : '',
        packageTitle: '',
        packageAmount: 0,
      };

      const response = await submitLead(payload);

      if (response.status === 200 && response.data.data.customerId) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${response.data.data.customerId}`;
        }, 1500);
      }
    } catch (err) {
      if (err.response) {
        const { status, data } = err.response;
        if (status === 401) {
          setError(data.message || 'Unauthorized access.');
        } else if (status === 400) {
          setError(data.errorMessage || 'Invalid data provided.');
        } else {
          setError('An error occurred. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lead-popup-overlay"
    >
      <div
        className="bg-white max-w-lg w-full p-4 md:p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-popup"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#2E2E2E] hover:text-[#2C3E50]"
          data-testid="close-popup-button"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-xl md:text-2xl font-bold text-[#2C3E50] mb-0.5">
          Get Legal Help
        </h2>
        <p className="text-[#2E2E2E]/70 text-xs md:text-sm mb-2 md:mb-4">
          Fill out the form below and our team will get back to you shortly.
        </p>

        {success ? (
          <div className="text-center py-8">
            <div className="text-green-600 font-semibold text-lg mb-2">Success!</div>
            <p className="text-[#2E2E2E]/70">Redirecting you now...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2 md:space-y-3">
            <div>
              <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
                className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-3 py-2 md:px-4 md:py-2.5 text-sm outline-none"
                data-testid="popup-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your.email@example.com"
                className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-3 py-2 md:px-4 md:py-2.5 text-sm outline-none"
                data-testid="popup-email-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-2 md:gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                  Country Code *
                </label>
                <select
                  name="countryDialCode"
                  value={formData.countryDialCode}
                  onChange={(e) => {
                    const selectedCode = countryCodes.find(c => c.code.replace('+', '') === e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      countryDialCode: e.target.value,
                      country: selectedCode ? selectedCode.countryCode : prev.country,
                    }));
                  }}
                  required
                  className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-2 py-2 md:px-4 md:py-2.5 text-sm outline-none"
                  data-testid="popup-country-code"
                >
                  {countryCodes.map((country, index) => (
                    <option key={index} value={country.code.replace('+', '')}>
                      {country.code} {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  maxLength="10"
                  placeholder="1234567890"
                  className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-3 py-2 md:px-4 md:py-2.5 text-sm outline-none"
                  data-testid="popup-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                Legal Need
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                placeholder="Describe your legal needs..."
                className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-3 py-2 md:px-4 md:py-2.5 text-sm outline-none resize-none"
                data-testid="popup-description-input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="popup-error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-2.5 md:py-3 font-medium text-sm md:text-base disabled:opacity-50"
              data-testid="popup-submit-button"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LeadPopup;
